/** Native media playback for the music. No SFX: nothing covers the songs. */
export default class AudioManager {
  constructor() {
    this.media = new Audio();
    this.media.preload = 'auto';
    this.media.playsInline = true;
    this.media.volume = 0.82;

    this._playing = false;
    this._ended = false;
    this._muted = false;
    this._song = null;
    this._preparedUrl = '';
    this._statusHandler = null;

    this.media.addEventListener('playing', () => {
      this._playing = true;
      this._ended = false;
      this._emitStatus('playing');
    });
    this.media.addEventListener('ended', () => {
      this._playing = false;
      this._ended = true;
      this._emitStatus('ended');
    });
    this.media.addEventListener('loadeddata', () => this._emitStatus('ready'));
    this.media.addEventListener('stalled', () => this._emitStatus('stalled'));
    this.media.addEventListener('error', () => {
      this._playing = false;
      this._emitStatus('error', 'Impossible de charger la piste audio.');
    });
  }

  setStatusHandler(handler) {
    this._statusHandler = handler;
  }

  _emitStatus(status, message = '') {
    this._statusHandler?.({ status, message, muted: this._muted });
  }

  /**
   * Light warm-up (catalog open): metadata only, no full download.
   * The actual track bytes are only fetched when a play starts.
   */
  warmSong(song) {
    if (!song?.audioUrl) return;
    this.media.preload = 'metadata';
    this.prepareSong(song);
  }

  prepareSong(song) {
    if (!song?.audioUrl) {
      this._emitStatus('error', 'Aucune piste audio configurée.');
      return;
    }
    const url = new URL(song.audioUrl, window.location.href).href;
    if (url !== this._preparedUrl) {
      this.media.pause();
      this.media.src = url;
      this._preparedUrl = url;
      this.media.load();
    }
    this._song = song;
    this._ended = false;
    this._emitStatus(this.media.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA ? 'ready' : 'loading');
  }

  /**
   * Must be called directly inside the click/touch handler.
   * audio.play() is invoked before any awaited work.
   */
  playPreparedSong(song) {
    this.media.preload = 'auto'; // full buffering only once a play actually starts
    this.prepareSong(song);
    try {
      this.media.currentTime = 0;
    } catch {
      // Seeking can fail before metadata; playback still starts at zero.
    }
    this._ended = false;
    const playPromise = this.media.play();
    this._playing = true;
    this._emitStatus('starting');
    playPromise?.catch((error) => {
      this._playing = false;
      this._emitStatus('error', `Lecture bloquée : ${error.message}`);
    });
    return playPromise;
  }

  get isReady() {
    return this.media.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && !this.media.error;
  }

  get currentTime() {
    return Number.isFinite(this.media.currentTime) ? this.media.currentTime : 0;
  }

  get isPlaying() {
    return this._playing && !this.media.paused;
  }

  get hasEnded() {
    return this._ended;
  }

  toggleMute() {
    this._muted = !this._muted;
    this.media.muted = this._muted;
    this._emitStatus(this._playing ? 'playing' : 'ready');
    return this._muted;
  }

  stop() {
    this._playing = false;
    this.media.pause();
    this.media.currentTime = 0;
  }

  getBeatPulse() {
    if (!this._song || !this._playing) return 0;
    const bpm = this._song.bpm || 120;
    const beat = 60 / bpm;
    const musicalTime = Math.max(0, this.currentTime + (this._song.offset || 0));
    const phase = (musicalTime % beat) / beat;
    return Math.max(0, 1 - phase * 3.2);
  }

  getSection() {
    if (!this._song) return 'intro';
    const bpm = this._song.bpm || 110;
    const bar = Math.floor(this.currentTime / ((60 / bpm) * 4));
    if (bar < 2) return 'intro';
    if (bar < 8) return 'verse';
    if (bar < 12) return 'build';
    if (bar < 20) return 'drop';
    if (bar < 24) return 'verse';
    if (bar < 28) return 'drop';
    return 'outro';
  }

}
